import type { FormFieldTemplate, FormField } from '@/types/form';

export const fieldTemplates: FormFieldTemplate[] = [
  {
    type: 'input',
    label: 'Text Input',
    icon: '📝',
    description: 'Single line text input field',
    defaultConfig: {
      type: 'input',
      label: 'Text Input',
      name: 'text_field',
      placeholder: 'Enter text...',
      required: false,
    },
  },
  {
    type: 'textarea',
    label: 'Text Area',
    icon: '📄',
    description: 'Multi-line text input field',
    defaultConfig: {
      type: 'textarea',
      label: 'Description',
      name: 'description',
      placeholder: 'Enter detailed description...',
      required: false,
      validation: {
        maxLength: 1000,
      },
    },
  },
  {
    type: 'select',
    label: 'Dropdown Select',
    icon: '⬇️',
    description: 'Single selection dropdown',
    defaultConfig: {
      type: 'select',
      label: 'Select Option',
      name: 'dropdown_field',
      required: false,
      options: [
        { value: 'option1', label: 'Option 1' },
        { value: 'option2', label: 'Option 2' },
        { value: 'option3', label: 'Option 3' },
      ],
    },
  },
  {
    type: 'radio',
    label: 'Radio Buttons',
    icon: '🔘',
    description: 'Single choice from multiple options',
    defaultConfig: {
      type: 'radio',
      label: 'Choose One',
      name: 'radio_field',
      required: false,
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        { value: 'maybe', label: 'Maybe' },
      ],
    },
  },
  {
    type: 'checkbox',
    label: 'Checkboxes',
    icon: '☑️',
    description: 'Multiple choice selection',
    defaultConfig: {
      type: 'checkbox',
      label: 'Select All That Apply',
      name: 'checkbox_field',
      required: false,
      options: [
        { value: 'option1', label: 'Option 1' },
        { value: 'option2', label: 'Option 2' },
        { value: 'option3', label: 'Option 3' },
      ],
    },
  },
  {
    type: 'number',
    label: 'Number Input',
    icon: '🔢',
    description: 'Numeric input field',
    defaultConfig: {
      type: 'number',
      label: 'Number',
      name: 'number_field',
      placeholder: '0',
      required: false,
      validation: {
        min: 0,
      },
    },
  },
  {
    type: 'date',
    label: 'Date Picker',
    icon: '📅',
    description: 'Date selection field',
    defaultConfig: {
      type: 'date',
      label: 'Select Date',
      name: 'date_field',
      required: false,
    },
  },
  {
    type: 'email',
    label: 'Email Input',
    icon: '📧',
    description: 'Email address input with validation',
    defaultConfig: {
      type: 'email',
      label: 'Email Address',
      name: 'email_field',
      placeholder: 'user@example.com',
      required: false,
      validation: {
        pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
        message: 'Please enter a valid email address',
      },
    },
  },
  {
    type: 'phone',
    label: 'Phone Number',
    icon: '📞',
    description: 'Phone number input with formatting',
    defaultConfig: {
      type: 'phone',
      label: 'Phone Number',
      name: 'phone_field',
      placeholder: '+1 (555) 123-4567',
      required: false,
      validation: {
        pattern: '^[+]?[1-9]?[0-9]{7,15}$',
        message: 'Please enter a valid phone number',
      },
    },
  },
  {
    type: 'url',
    label: 'URL Input',
    icon: '🔗',
    description: 'Website URL input field',
    defaultConfig: {
      type: 'url',
      label: 'Website URL',
      name: 'url_field',
      placeholder: 'https://example.com',
      required: false,
      validation: {
        pattern: '^https?:\\/\\/[^\\s]+$',
        message: 'Please enter a valid URL',
      },
    },
  },
  {
    type: 'file',
    label: 'File Upload',
    icon: '📎',
    description: 'File upload field with type and size validation',
    defaultConfig: {
      type: 'file',
      label: 'Upload File',
      name: 'file_field',
      required: false,
      validation: {
        message: 'Please select a valid file',
        allowedTypes: ['image/*', 'application/pdf', '.doc', '.docx', '.txt'],
        maxSizeBytes: 10485760, // 10MB
      },
      placeholder: 'Choose file...',
    },
  },
];

export const formTemplates: Array<{
  id: string;
  name: string;
  description: string;
  category: 'report' | 'project' | 'survey' | 'application' | 'other';
  fields: Omit<FormField, 'id' | 'sortOrder'>[];
}> = [
  {
    id: 'project-report',
    name: 'Project Report Form',
    description: 'Standard template for project reporting',
    category: 'report',
    fields: [
      {
        type: 'input',
        name: 'project_name',
        label: 'Project Name',
        placeholder: 'Enter project name',
        required: true,
      },
      {
        type: 'select',
        name: 'project_status',
        label: 'Project Status',
        required: true,
        options: [
          { value: 'planning', label: 'Planning' },
          { value: 'in_progress', label: 'In Progress' },
          { value: 'review', label: 'Under Review' },
          { value: 'completed', label: 'Completed' },
          { value: 'on_hold', label: 'On Hold' },
        ],
      },
      {
        type: 'date',
        name: 'report_date',
        label: 'Report Date',
        required: true,
      },
      {
        type: 'textarea',
        name: 'summary',
        label: 'Executive Summary',
        placeholder: 'Provide a brief overview of the project status...',
        required: true,
        validation: {
          maxLength: 1000,
        },
      },
      {
        type: 'textarea',
        name: 'achievements',
        label: 'Key Achievements',
        placeholder: 'List major accomplishments since last report...',
        required: false,
      },
      {
        type: 'textarea',
        name: 'challenges',
        label: 'Challenges & Issues',
        placeholder: 'Describe any obstacles or concerns...',
        required: false,
      },
      {
        type: 'textarea',
        name: 'next_steps',
        label: 'Next Steps',
        placeholder: 'Outline upcoming activities and milestones...',
        required: false,
      },
    ],
  },
  {
    id: 'new-project',
    name: 'New Project Application',
    description: 'Form for submitting new project proposals',
    category: 'project',
    fields: [
      {
        type: 'input',
        name: 'project_title',
        label: 'Project Title',
        placeholder: 'Enter descriptive project title',
        required: true,
        validation: {
          minLength: 10,
          maxLength: 100,
        },
      },
      {
        type: 'select',
        name: 'category',
        label: 'Project Category',
        required: true,
        options: [
          { value: 'research', label: 'Research & Development' },
          { value: 'infrastructure', label: 'Infrastructure' },
          { value: 'software', label: 'Software Development' },
          { value: 'marketing', label: 'Marketing & Outreach' },
          { value: 'operations', label: 'Operations' },
          { value: 'other', label: 'Other' },
        ],
      },
      {
        type: 'textarea',
        name: 'description',
        label: 'Project Description',
        placeholder: 'Provide detailed description of the project...',
        required: true,
        validation: {
          minLength: 100,
          maxLength: 2000,
        },
      },
      {
        type: 'textarea',
        name: 'objectives',
        label: 'Project Objectives',
        placeholder: 'List the main goals and expected outcomes...',
        required: true,
      },
      {
        type: 'number',
        name: 'budget',
        label: 'Estimated Budget ($)',
        placeholder: '0',
        required: true,
        validation: {
          min: 0,
          max: 1000000,
        },
      },
      {
        type: 'date',
        name: 'start_date',
        label: 'Proposed Start Date',
        required: true,
      },
      {
        type: 'date',
        name: 'end_date',
        label: 'Expected Completion Date',
        required: true,
      },
      {
        type: 'email',
        name: 'contact_email',
        label: 'Primary Contact Email',
        placeholder: 'project.lead@company.com',
        required: true,
      },
    ],
  },
  {
    id: 'user-survey',
    name: 'User Feedback Survey',
    description: 'Template for collecting user feedback and satisfaction',
    category: 'survey',
    fields: [
      {
        type: 'input',
        name: 'name',
        label: 'Your Name',
        placeholder: 'Enter your full name',
        required: false,
      },
      {
        type: 'email',
        name: 'email',
        label: 'Email Address',
        placeholder: 'your.email@example.com',
        required: false,
      },
      {
        type: 'radio',
        name: 'satisfaction',
        label: 'Overall Satisfaction',
        required: true,
        options: [
          { value: 'very_satisfied', label: 'Very Satisfied' },
          { value: 'satisfied', label: 'Satisfied' },
          { value: 'neutral', label: 'Neutral' },
          { value: 'dissatisfied', label: 'Dissatisfied' },
          { value: 'very_dissatisfied', label: 'Very Dissatisfied' },
        ],
      },
      {
        type: 'checkbox',
        name: 'features_used',
        label: 'Which features do you use?',
        required: false,
        options: [
          { value: 'dashboard', label: 'Dashboard' },
          { value: 'reporting', label: 'Reporting' },
          { value: 'analytics', label: 'Analytics' },
          { value: 'user_management', label: 'User Management' },
          { value: 'api', label: 'API Integration' },
        ],
      },
      {
        type: 'textarea',
        name: 'feedback',
        label: 'Additional Comments',
        placeholder: 'Please share any additional feedback or suggestions...',
        required: false,
        validation: {
          maxLength: 1000,
        },
      },
      {
        type: 'radio',
        name: 'recommend',
        label: 'Would you recommend us to others?',
        required: true,
        options: [
          { value: 'definitely', label: 'Definitely' },
          { value: 'probably', label: 'Probably' },
          { value: 'not_sure', label: 'Not Sure' },
          { value: 'probably_not', label: 'Probably Not' },
          { value: 'definitely_not', label: 'Definitely Not' },
        ],
      },
    ],
  },
];

export function createFieldFromTemplate(template: FormFieldTemplate, customName?: string): Omit<FormField, 'id' | 'sortOrder'> {
  return {
    ...template.defaultConfig,
    name: customName || template.defaultConfig.name || template.type + '_field',
  } as Omit<FormField, 'id' | 'sortOrder'>;
}

export function createFormFromTemplate(templateId: string, customName?: string): Omit<FormField, 'id' | 'sortOrder'>[] {
  const template = formTemplates.find(t => t.id === templateId);
  if (!template) {
    throw new Error(`Form template with id ${templateId} not found`);
  }
  
  return template.fields.map((field) => ({
    ...field,
    name: customName ? `${customName}_${field.name}` : field.name,
  }));
}